// Render variant options (size/color, etc.) directly on listing cards, Amazon-style,
// instead of sending the shopper to the "Select Variant" popup on the PDP.
// Reuses webshop's existing whitelisted variant-selector APIs and the existing
// .btn-add-to-cart-list click pipeline (shopping_cart.js + listing_quantity.js) -
// we only need to populate that button's data-item-code once a variant is resolved.
//
// Page-agnostic and card-centric by design: this does NOT patch any specific
// renderer (webshop.ProductGrid/ProductList, infinite_scroll.js, or anything
// else that might render a card in the future). Instead it watches the DOM
// for any ".item-card" appearing anywhere - the listing page, a homepage
// carousel, search results, infinite scroll - and independently asks the
// backend "does this one need swatches?" per card. A card that was rendered
// by a script that doesn't even know about variants (and so showed a plain
// Add to Cart button pointing at a template item_code, which is broken -
// you can't add a template to the cart) still gets corrected, because the
// check isn't tied to how the card got there.
import { resolveSwatchColor, getSwatchColorOverrides } from './swatch_colors_bridge.js';

frappe.ready(function () {
	if (!window.webshop) return;

	// Some templates have duplicate "Item Variant Attribute" rows (e.g. two
	// rows for "Size" on the same template) from past data imports, which
	// makes the API return the same attribute twice. Collapse those so the
	// card doesn't render a repeated row of pills.
	function dedupeAttributes(attributes) {
		var seen = {};
		var result = [];
		(attributes || []).forEach(function (attr) {
			if (seen[attr.attribute]) return;
			seen[attr.attribute] = true;
			result.push(attr);
		});
		return result;
	}

	// --- Cart settings (enable_checkout / enabled), fetched once and shared
	// across every card regardless of which one triggers the fetch first.

	var cachedCartSettings = null;
	var pendingSettingsCallbacks = [];

	function withCartSettings(callback) {
		if (cachedCartSettings) {
			callback(cachedCartSettings);
			return;
		}
		pendingSettingsCallbacks.push(callback);
		if (pendingSettingsCallbacks.length > 1) return; // a fetch is already in flight

		frappe.call({
			method: 'webshop.webshop.api.get_product_filter_data',
			args: { query_args: { start: 0 } },
			callback: function (r) {
				cachedCartSettings = (r && r.message && r.message.settings) || {};
				var queued = pendingSettingsCallbacks;
				pendingSettingsCallbacks = [];
				queued.forEach(function (cb) { cb(cachedCartSettings); });
			}
		});
	}

	// --- Card discovery: independent of which script rendered the card ---

	function getCardRoute(card) {
		var link = card.querySelector('a[href]');
		if (!link) return null;
		var href = link.getAttribute('href') || '';
		// strip origin/query/hash if present, keep just the path
		href = href.replace(/^https?:\/\/[^/]+/, '').split('?')[0].split('#')[0];
		return href.replace(/^\/+/, '').replace(/\/+$/, '');
	}

	var pendingRoutes = {}; // route -> [card, ...]
	var batchTimer = null;

	function queueCardForCheck(card) {
		// raftor_websitehq draws this card and renders its own colour dots.
		// Skipping here (rather than at inject time) also spares the batched
		// attribute request for it.
		if (window.webhqOwnedSurface && window.webhqOwnedSurface(card)) return;
		if (card.dataset.raftorVariantChecked === '1') return;
		card.dataset.raftorVariantChecked = '1';

		var route = getCardRoute(card);
		if (!route) return;

		if (!pendingRoutes[route]) pendingRoutes[route] = [];
		pendingRoutes[route].push(card);

		if (batchTimer) return;
		// Small debounce so a burst of cards appearing together (initial
		// render, a full page of infinite-scroll results) becomes one
		// request instead of one per card.
		batchTimer = setTimeout(flushCardBatch, 60);
	}

	function flushCardBatch() {
		batchTimer = null;
		var batch = pendingRoutes;
		pendingRoutes = {};
		var routes = Object.keys(batch);
		if (!routes.length) return;

		frappe.call({
			method: 'raftor_webhq.catalog.api.get_variant_attributes_for_routes',
			args: { routes: routes },
			callback: function (r) {
				var result = (r && r.message) || {};
				var matchedRoutes = Object.keys(result);
				if (!matchedRoutes.length) return;

				withCartSettings(function (settings) {
					if (!settings || !settings.enabled) return;
					getSwatchColorOverrides().then(function (swatchColorOverrides) {
						matchedRoutes.forEach(function (route) {
							var entry = result[route];
							var attributes = dedupeAttributes(entry && entry.attributes);
							if (!entry || !entry.item_code || !attributes.length) return;

							(batch[route] || []).forEach(function (card) {
								if (!card.isConnected) return; // removed from DOM since queued
								injectSwatches(card, entry.item_code, attributes, settings, swatchColorOverrides);
							});
						});
					});
				});
			}
		});
	}

	function scanForCards(root) {
		if (!root || root.nodeType !== 1) return;
		if (root.matches && root.matches('.item-card')) queueCardForCheck(root);
		if (root.querySelectorAll) {
			Array.prototype.forEach.call(root.querySelectorAll('.item-card'), queueCardForCheck);
		}
	}

	// --- Building & injecting the swatch block ---

	function isColourAttribute(name) {
		return /^colou?r$/i.test(String(name || '').trim());
	}

	function renderSwatchPillsHtml(attributes) {
		var html = '';
		(attributes || []).forEach(function (attr) {
			if (!attr.values || !attr.values.length) return;
			// Colour is rendered separately, as an unlabeled overlay directly on
			// the product image - see renderColourOverlayHtml/injectSwatches.
			if (isColourAttribute(attr.attribute)) return;
			html += '<div class="variant-attr-row" data-attribute="' + frappe.utils.escape_html(attr.attribute) + '">';
			html += '<span class="variant-attr-label">' + frappe.utils.escape_html(attr.attribute) + '</span>';
			html += '<div class="variant-pill-group">';
			attr.values.forEach(function (value) {
				var safeValue = frappe.utils.escape_html(value);
				html += '<button type="button" class="variant-pill"' +
					' data-attribute="' + frappe.utils.escape_html(attr.attribute) + '"' +
					' data-value="' + safeValue + '">' +
					safeValue +
				'</button>';
			});
			html += '</div></div>';
		});
		html += '<div class="variant-price small"></div>';
		return html;
	}

	// Colour renders as an unlabeled overlay on the product image instead of a
	// normal card-body row (CSS Grid stacking in .card-img-container, see
	// webhq_overrides.css - no position:absolute, no guessed pixel offsets, so
	// it's always correctly contained regardless of that photo's aspect ratio).
	// Same .variant-pill contract as every other pill so selection/stock/price
	// wiring works unchanged; the caller places this markup in the image
	// container rather than card-body.
	function renderColourOverlayHtml(attributes, swatchColorOverrides) {
		var colourAttr = null;
		(attributes || []).forEach(function (attr) {
			if (colourAttr) return;
			if (attr.values && attr.values.length && isColourAttribute(attr.attribute)) {
				colourAttr = attr;
			}
		});
		if (!colourAttr) return '';

		var html = '<div class="variant-attr-row" data-attribute="' + frappe.utils.escape_html(colourAttr.attribute) + '">';
		html += '<div class="variant-pill-group">';
		colourAttr.values.forEach(function (value) {
			var safeValue = frappe.utils.escape_html(value);
			var swatchColor = resolveSwatchColor(value, swatchColorOverrides) || '#c4c4c4';
			html += '<button type="button" class="variant-pill variant-pill--color"' +
				' data-attribute="' + frappe.utils.escape_html(colourAttr.attribute) + '"' +
				' data-value="' + safeValue + '"' +
				' title="' + safeValue + '" aria-label="' + safeValue + '"' +
				' style="--swatch-color: ' + swatchColor + '">' +
				'<span class="sr-only">' + safeValue + '</span>' +
			'</button>';
		});
		html += '</div></div>';
		return html;
	}

	// Whatever the original renderer put in the card-body for the primary
	// action - webshop's own "Explore" link, or a plain Add to Cart/Go to
	// Cart pair from a renderer that doesn't know about variants (broken:
	// it points at the template's own item_code) - gets replaced.
	function removeExistingPrimaryAction(cardBody) {
		var explore = cardBody.querySelector('.btn-explore-variants');
		if (explore) {
			var exploreTarget = explore.closest('a') || explore;
			if (exploreTarget.parentNode) exploreTarget.parentNode.removeChild(exploreTarget);
		}

		Array.prototype.forEach.call(
			cardBody.querySelectorAll('.btn-add-to-cart-list, .cart-indicator'),
			function (el) {
				var wrapperLink = el.closest('a');
				var target = (wrapperLink && wrapperLink !== cardBody) ? wrapperLink : el;
				if (target.parentNode) target.parentNode.removeChild(target);
			}
		);
	}

	function injectSwatches(card, itemCode, attributes, settings, swatchColorOverrides) {
		var cardBody = card.querySelector('.card-body') || card.querySelector('.card');
		if (!cardBody) return;
		// Already has swatches for this exact item (e.g. queued twice in
		// edge-case timing) - don't duplicate.
		if (cardBody.querySelector('.variant-swatches[data-template-item-code="' + itemCode + '"]')) return;

		removeExistingPrimaryAction(cardBody);

		var enableCheckout = settings.enable_checkout ? 1 : 0;
		var gotoLabel = settings.enable_checkout ? __("Go to Cart") : __("Go to Quote");

		var swatchContainer = document.createElement('div');
		swatchContainer.className = 'variant-swatches';
		swatchContainer.setAttribute('data-template-item-code', itemCode);
		swatchContainer.setAttribute('data-enable-checkout', String(enableCheckout));
		swatchContainer.innerHTML = renderSwatchPillsHtml(attributes);
		cardBody.appendChild(swatchContainer);

		// Colour pill(s), if any, are a real DOM child of .card-img-container
		// (not card-body) so CSS Grid stacking can contain them within the
		// actual image box. Pill-selection queries throughout this file are
		// scoped to the whole card (not just .variant-swatches) precisely so
		// this pill is still found regardless of which container it lives in.
		var colourRowHtml = renderColourOverlayHtml(attributes, swatchColorOverrides);
		if (colourRowHtml) {
			var imgContainer = card.querySelector('.card-img-container');
			if (imgContainer) {
				var colourRowWrapper = document.createElement('div');
				colourRowWrapper.innerHTML = colourRowHtml;
				var colourRow = colourRowWrapper.firstElementChild;
				if (colourRow) imgContainer.appendChild(colourRow);
			}
		}

		var addBtn = document.createElement('div');
		addBtn.className = 'btn btn-sm btn-primary btn-add-to-cart-list variant-add-btn variant-pending w-100 mt-2';
		addBtn.setAttribute('data-item-code', '');
		addBtn.setAttribute('data-template-item-code', itemCode);
		addBtn.textContent = settings.enable_checkout ? __("Add to Cart") : __("Add to Quote");
		cardBody.appendChild(addBtn);

		var gotoLink = document.createElement('a');
		gotoLink.setAttribute('href', '/cart');
		var gotoBtn = document.createElement('div');
		gotoBtn.className = 'btn btn-sm btn-primary btn-add-to-cart-list variant-goto-btn go-to-cart-grid go-to-cart hidden';
		gotoBtn.setAttribute('data-item-code', '');
		gotoBtn.setAttribute('data-template-item-code', itemCode);
		gotoBtn.textContent = gotoLabel;
		gotoLink.appendChild(gotoBtn);
		cardBody.appendChild(gotoLink);

		applyCartPreselection(swatchContainer);
		applyDefaultPreselection(swatchContainer);
	}

	// --- Pre-select swatches that already match a variant in the shopper's
	// cart, so revisiting the listing doesn't reset their choice every time.
	// Cart -> grid direction of the sync; grid -> cart (clicking Add to Cart)
	// already worked via the existing .btn-add-to-cart-list pipeline.
	//
	// Selections can come from three sources, in increasing priority:
	// 'default' (first value per attribute row, picked automatically so the
	// card always shows a price/Add to Cart instead of an idle picker),
	// 'cart' (matches what's actually in the shopper's cart), and 'user' (an
	// explicit click). A lower-priority source is allowed to be overwritten
	// by a higher one, but never the reverse - e.g. the cart sync arriving
	// after a default pick has already been applied should still correct it,
	// but must never clobber a choice the shopper made by hand.

	function getSelectionSource(container) {
		return container.dataset.selectionSource || '';
	}

	// Pill-selection helpers below are scoped to the whole card (not just
	// .variant-swatches), because colour pills live in .card-img-container -
	// a sibling, not a descendant, of .variant-swatches. Safe: one .item-card
	// never has more than one swatch set, and .variant-pill is produced by
	// exactly one code path, so card-scoped and swatches-scoped queries
	// return the identical node set for every other attribute anyway.
	function clearActivePills(card) {
		Array.prototype.forEach.call(card.querySelectorAll('.variant-pill.active'), function (pill) {
			pill.classList.remove('active');
		});
	}

	var cartVariantSelections = {}; // template_item_code -> {item_code, attributes}
	var cartVariantSelectionsRefreshTimer = null;

	// Debounced like listing_quantity.js's own refreshListingFromServerCart -
	// resolving a pre-selected variant triggers a 'cart-updated' event (to
	// sync the qty spinner), which would otherwise re-trigger this same
	// refresh in a loop. Collapsing bursts into one call after a short quiet
	// period keeps it self-stabilizing instead of chaining network round-trips.
	function refreshCartVariantSelections() {
		// Same fix as listing_quantity.js's own refreshListingFromServerCart:
		// this ran unconditionally on page boot AND on every 'cart-updated'
		// event (i.e. after every qty tap anywhere, including /cart's own
		// stepper -- cart_resilience.js fires that event globally), calling
		// get_cart_items_snapshot + get_variant_selections_for_items for
		// zero benefit on any page without a single variant-swatch card --
		// /cart included, which has none. Found live via network trace:
		// this and the listing-side call were the dominant extra latency on
		// a shop->cart navigation, not the cart page's own rendering.
		if (!document.querySelector('.variant-swatches[data-template-item-code], .item-card')) {
			return;
		}
		if (cartVariantSelectionsRefreshTimer) return;
		cartVariantSelectionsRefreshTimer = setTimeout(function () {
			cartVariantSelectionsRefreshTimer = null;
			doRefreshCartVariantSelections();
		}, 200);
	}

	function doRefreshCartVariantSelections() {
		frappe.call({
			method: 'raftor_webhq.checkout.simple_checkout.get_cart_items_snapshot',
			callback: function (r) {
				var items = (r && r.message && r.message.items) || [];
				var itemCodes = items.map(function (row) { return row.item_code; }).filter(Boolean);

				if (!itemCodes.length) {
					cartVariantSelections = {};
					return;
				}

				frappe.call({
					method: 'raftor_webhq.catalog.api.get_variant_selections_for_items',
					args: { item_codes: itemCodes },
					callback: function (r2) {
						cartVariantSelections = (r2 && r2.message) || {};
						Array.prototype.forEach.call(
							document.querySelectorAll('.variant-swatches[data-template-item-code]'),
							applyCartPreselection
						);
					}
				});
			}
		});
	}

	function applyCartPreselection(container) {
		var templateItemCode = container.getAttribute('data-template-item-code');
		var selection = templateItemCode && cartVariantSelections[templateItemCode];
		if (!selection || !selection.attributes) return;

		// Don't clobber a choice the shopper has already made on this visit,
		// but a same-or-lower priority 'default' auto-pick is fair game.
		if (getSelectionSource(container) === 'user') return;

		var card = container.closest('.item-card, .list-row');
		if (!card) return;

		var attributes = selection.attributes;
		clearActivePills(card);
		clearOosPills(card);
		delete container._stockSearch;
		var appliedAny = false;
		Array.prototype.forEach.call(card.querySelectorAll('.variant-pill'), function (pill) {
			var attribute = pill.getAttribute('data-attribute');
			var value = pill.getAttribute('data-value');
			if (Object.prototype.hasOwnProperty.call(attributes, attribute) && attributes[attribute] === value) {
				pill.classList.add('active');
				appliedAny = true;
			}
		});

		if (appliedAny) {
			container.dataset.selectionSource = 'cart';
			// skipResync: we already know this exact item is in the cart (that's
			// why we're pre-selecting it) - no need to ask listing_quantity.js to
			// re-check. Without this, resolving would fire 'cart-updated', which
			// we also listen for, re-fetching this same selection data in a loop.
			resolveVariant(card, templateItemCode, /* skipResync */ true);
		}
	}

	// Auto-pick the first value of every attribute row (e.g. the first
	// size) so a card always lands with a price and an active "Add to Cart"
	// button instead of an idle "pick a size first" state. Only applies when
	// nothing else has claimed a selection yet - a cart match or a user
	// click both take priority and are applied separately.
	function applyDefaultPreselection(container) {
		if (getSelectionSource(container)) return;

		var card = container.closest('.item-card, .list-row');
		if (!card) return;
		if (card.querySelector('.variant-pill.active')) return;

		var appliedAny = false;
		Array.prototype.forEach.call(card.querySelectorAll('.variant-attr-row'), function (row) {
			var firstPill = row.querySelector('.variant-pill');
			if (firstPill) {
				firstPill.classList.add('active');
				appliedAny = true;
			}
		});

		if (appliedAny) {
			container.dataset.selectionSource = 'default';
			resolveVariant(card, container.getAttribute('data-template-item-code'));
		}
	}

	// listing_quantity.js keys its qty-spinner UI off whatever item-code was
	// resolved at the moment "Add to Cart" was clicked, and hides the Add to
	// Cart button by *DOM node* (not by item-code). When a shopper switches
	// swatches after that, our button's data-item-code moves to a different
	// item, but the old qty-wrapper/hidden-state for the previous variant is
	// left behind. Clear that stale UI on every switch so the card always
	// reflects the cart state of the *currently selected* variant, not
	// whichever variant happened to be picked previously.
	function clearStaleListingCartUi(card) {
		var staleWrapper = card.querySelector('.listing-qty-wrapper');
		if (staleWrapper) staleWrapper.parentNode.removeChild(staleWrapper);

		var staleIndicator = card.querySelector('.cart-indicator');
		if (staleIndicator) staleIndicator.classList.add('hidden');
	}

	// Re-runs listing_quantity.js's live-cart sync (via the same event it
	// already listens for) so the card picks up the real qty for whichever
	// item-code is now on the Add to Cart button.
	function triggerCartResync() {
		if (window.jQuery && typeof window.jQuery === 'function') {
			window.jQuery(document).trigger('cart-updated');
		}
	}

	// Swap the card's own photo to the resolved variant's image, same fix as
	// raftor_webhq's pdp_variant_swatches.js and raftor_websitehq's grid.js.
	// Mutates the existing <img>'s src directly rather than replacing it:
	// raftor_websitehq's image_zoom_hover.js binds this exact node once per
	// card (a closure plus a MutationObserver on its `src` attribute) and
	// marks the container `data-zoom-initialized` so it's never rebound -
	// swapping the node itself would silently break hover-zoom for any card
	// already initialized by the time a swatch is clicked.
	function updateCardImage(card, imageUrl) {
		if (!imageUrl) return;
		var img = card.querySelector('.card-img-container img.card-img');
		if (!img || img.getAttribute('src') === imageUrl) return;
		img.setAttribute('src', imageUrl);
	}

	function setButtonPending(card, addBtn, gotoBtn, label) {
		clearStaleListingCartUi(card);

		addBtn.classList.remove('hidden');
		addBtn.classList.add('variant-pending');
		addBtn.classList.remove('variant-out-of-stock');
		addBtn.removeAttribute('id');
		addBtn.setAttribute('data-item-code', '');
		addBtn.textContent = label;

		if (gotoBtn) {
			gotoBtn.classList.add('hidden');
			gotoBtn.removeAttribute('id');
			gotoBtn.setAttribute('data-item-code', '');
		}
	}

	function setButtonResolved(card, addBtn, gotoBtn, itemCode, enableCheckout, skipResync) {
		// Once a variant is matched and resolveVariant has confirmed it's
		// actually in stock (see the out-of-stock handling above its call
		// site), Add to Cart is shown unconditionally - matching the PDP
		// "Select Variant" dialog.
		addBtn.classList.remove('variant-out-of-stock');
		var isNewSelection = addBtn.getAttribute('data-item-code') !== itemCode;
		if (isNewSelection) {
			// Only reset visibility/qty-UI when the resolved item actually
			// changed - if it's the same item as before, leave whatever
			// hidden/qty-spinner state listing_quantity.js has already set
			// (it reflects the real, current cart contents) untouched.
			clearStaleListingCartUi(card);
			addBtn.classList.remove('hidden');
		}

		addBtn.setAttribute('data-item-code', itemCode);
		addBtn.setAttribute('id', itemCode);
		if (gotoBtn) {
			gotoBtn.setAttribute('data-item-code', itemCode);
			gotoBtn.setAttribute('id', itemCode);
		}

		addBtn.classList.remove('variant-pending');
		addBtn.textContent = enableCheckout ? __("Add to Cart") : __("Add to Quote");

		if (isNewSelection && !skipResync) {
			// Pick up the real cart qty for this specific variant, if any -
			// this is what shows the qty spinner instead of a plain Add to Cart
			// button when the shopper has already added this exact size before.
			triggerCartResync();
		}
	}

	function clearOosPills(card) {
		Array.prototype.forEach.call(card.querySelectorAll('.variant-pill-oos'), function (pill) {
			pill.classList.remove('variant-pill-oos');
		});
	}

	function getActiveSelection(card) {
		var selection = {};
		Array.prototype.forEach.call(card.querySelectorAll('.variant-pill.active'), function (pill) {
			selection[pill.getAttribute('data-attribute')] = pill.getAttribute('data-value');
		});
		return selection;
	}

	function applySelection(card, selection) {
		Array.prototype.forEach.call(card.querySelectorAll('.variant-pill'), function (pill) {
			var attribute = pill.getAttribute('data-attribute');
			var value = pill.getAttribute('data-value');
			pill.classList.toggle('active', selection[attribute] === value);
		});
	}

	function selectionKey(selection) {
		return Object.keys(selection).sort().map(function (attribute) {
			return attribute + '=' + selection[attribute];
		}).join('&');
	}

	// Cap on how many attribute combinations a single card will probe while
	// hunting for an in-stock one, so a sparse/mostly-sold-out variant matrix
	// (many sizes x many colors) can't turn into dozens of sequential
	// network round-trips on one card.
	var MAX_STOCK_SEARCH_COMBINATIONS = 24;

	// Builds every attribute combination for this template, ordered to
	// explore outward from what's currently selected: the row that just
	// changed (changedAttribute) cycles through its own values first while
	// every other row stays at the shopper's actual pick; only once that's
	// exhausted does another row move away from the shopper's pick, one
	// step at a time.
	function buildStockSearchOrder(card, changedAttribute, currentSelection) {
		var rows = [];
		Array.prototype.forEach.call(card.querySelectorAll('.variant-attr-row'), function (row) {
			var attribute = row.getAttribute('data-attribute');
			var values = Array.prototype.map.call(row.querySelectorAll('.variant-pill'), function (pill) {
				return pill.getAttribute('data-value');
			});
			var startIndex = Math.max(0, values.indexOf(currentSelection[attribute]));
			rows.push({
				attribute: attribute,
				values: values.slice(startIndex).concat(values.slice(0, startIndex))
			});
		});

		rows.sort(function (a, b) {
			if (a.attribute === changedAttribute) return -1;
			if (b.attribute === changedAttribute) return 1;
			return 0;
		});

		var combos = [{}];
		rows.forEach(function (rowInfo) {
			var next = [];
			rowInfo.values.forEach(function (value) {
				combos.forEach(function (combo) {
					var copy = {};
					for (var key in combo) copy[key] = combo[key];
					copy[rowInfo.attribute] = value;
					next.push(copy);
				});
			});
			combos = next;
		});

		return combos.slice(0, MAX_STOCK_SEARCH_COMBINATIONS);
	}

	// A resolved exact_match can still be unbuyable (0 stock, stock-tracked,
	// and the merchant hasn't enabled "allow_items_not_in_stock") - and a
	// fully-specified combination can also simply not exist as a real item.
	// Either way, rather than show an active "Add to Cart" for something the
	// cart will reject (see raftor_webhq's stock_guard.py override of
	// update_cart), step to the next candidate combination instead of
	// settling on this one. If exhausting every value of the changed row
	// alone doesn't find anything, this widens the search to other
	// attributes too (e.g. try other colors, not just other sizes), up to
	// MAX_STOCK_SEARCH_COMBINATIONS, before giving up with a terminal
	// "Out of Stock" state.
	function advanceStockSearch(card, container, addBtn, gotoBtn, priceEl, templateItemCode, changedAttribute, skipResync) {
		var current = getActiveSelection(card);

		if (!container._stockSearch) {
			var rowAttr = changedAttribute;
			if (!rowAttr) {
				var firstRow = card.querySelector('.variant-attr-row');
				rowAttr = firstRow && firstRow.getAttribute('data-attribute');
			}
			container._stockSearch = {
				changedAttribute: rowAttr,
				baseline: current, // the shopper's actual picks when the hunt started
				combos: buildStockSearchOrder(card, rowAttr, current),
				index: 0,
				tried: {}
			};
		}
		var search = container._stockSearch;

		search.tried[selectionKey(current)] = true;

		// A failure where every *other* attribute still matches the baseline
		// (the shopper's actual picks when this hunt started) is an
		// unambiguous "this exact value has no stock given the rest of the
		// selection" - safe to grey out permanently. Once the search has
		// moved on to varying another attribute too, we stop marking
		// individual pills, since "bad" in that wider context no longer
		// reflects the shopper's real choices.
		var isFirstLapFailure = Object.keys(search.baseline).every(function (attribute) {
			return attribute === search.changedAttribute || current[attribute] === search.baseline[attribute];
		});
		if (isFirstLapFailure) {
			var changedRow = card.querySelector('.variant-attr-row[data-attribute="' + search.changedAttribute + '"]');
			if (changedRow) {
				Array.prototype.forEach.call(changedRow.querySelectorAll('.variant-pill'), function (pill) {
					if (pill.getAttribute('data-value') === current[search.changedAttribute]) {
						pill.classList.remove('active');
						pill.classList.add('variant-pill-oos');
					}
				});
			}
		}

		for (; search.index < search.combos.length; search.index++) {
			var candidate = search.combos[search.index];
			var key = selectionKey(candidate);
			if (search.tried[key]) continue;
			search.tried[key] = true;
			search.index++;
			applySelection(card, candidate);
			resolveVariant(card, templateItemCode, skipResync, search.changedAttribute);
			return;
		}

		delete container._stockSearch;
		setButtonPending(card, addBtn, gotoBtn, __("Out of Stock"));
		addBtn.classList.add('variant-out-of-stock');
		if (priceEl) priceEl.innerHTML = '';
	}

	function resolveVariant(card, templateItemCode, skipResync, changedAttribute) {
		var container = card.querySelector('.variant-swatches[data-template-item-code="' + templateItemCode + '"]');
		var addBtn = card.querySelector('.variant-add-btn[data-template-item-code="' + templateItemCode + '"]');
		var gotoBtn = card.querySelector('.variant-goto-btn[data-template-item-code="' + templateItemCode + '"]');
		if (!container || !addBtn) return;

		var priceEl = container.querySelector('.variant-price');

		var selected = getActiveSelection(card);

		// Tag this request so a slower, now-superseded response can't land
		// after a newer one and overwrite the button with stale data - e.g.
		// clicking 28 then quickly 36 could otherwise have the 28 response
		// arrive second and leave "Add to Cart" pointing at the wrong item
		// while pill 36 shows as the active selection.
		var requestId = (parseInt(container.dataset.requestId || '0', 10) || 0) + 1;
		container.dataset.requestId = String(requestId);

		if (!Object.keys(selected).length) {
			var idleEnableCheckout = container.getAttribute('data-enable-checkout') === '1';
			setButtonPending(card, addBtn, gotoBtn, idleEnableCheckout ? __("Add to Cart") : __("Add to Quote"));
			if (priceEl) priceEl.innerHTML = '';
			Array.prototype.forEach.call(card.querySelectorAll('.variant-pill'), function (pill) {
				pill.classList.remove('variant-pill-disabled');
			});
			return;
		}

		frappe.call({
			method: 'webshop.webshop.variant_selector.utils.get_next_attribute_and_values',
			args: {
				item_code: templateItemCode,
				selected_attributes: selected
			},
			callback: function (r) {
				// A newer request was fired for this card before this one
				// returned - discard the stale result.
				if (String(requestId) !== container.dataset.requestId) return;

				var data = r && r.message;
				if (!data) return;

				// Note: the underlying API narrows an already-selected attribute down to
				// just the chosen value (it's built for the PDP dialog's "Clear" flow).
				// On a card there's no Clear affordance, so pills within a row the
				// shopper has already picked stay switchable; only *other*, unselected
				// attribute rows get narrowed based on the current pick.
				var validOptions = data.valid_options_for_attributes || {};
				Array.prototype.forEach.call(card.querySelectorAll('.variant-pill'), function (pill) {
					var attribute = pill.getAttribute('data-attribute');
					var value = pill.getAttribute('data-value');
					if (Object.prototype.hasOwnProperty.call(selected, attribute)) {
						pill.classList.remove('variant-pill-disabled');
						return;
					}
					var validValues = validOptions[attribute];
					var isValid = validValues ? validValues.indexOf(value) !== -1 : true;
					pill.classList.toggle('variant-pill-disabled', !isValid);
				});

				var enableCheckout = container.getAttribute('data-enable-checkout') === '1';

				if (data.exact_match && data.exact_match.length === 1) {
					var matchedItemCode = data.exact_match[0];
					var info = data.product_info;

					// get_next_attribute_and_values' own available_qty can't be
					// trusted here: it looks up the resolved variant's own
					// Website Item.website_warehouse, but this catalog only
					// publishes the *template* as a Website Item (no per-variant
					// Website Item exists), so that lookup silently resolves to
					// no warehouse and always returns 0 - reporting every variant
					// as out of stock regardless of real stock. raftor_webhq's
					// own stock helpers (used by cart validation and the PDP)
					// correctly fall back to the template's warehouse, so confirm
					// real availability through that same path before deciding.
					frappe.call({
						method: 'raftor_webhq.catalog.api.get_product_info',
						args: { item_code: matchedItemCode, skip_quotation_creation: 1 },
						callback: function (r2) {
							if (String(requestId) !== container.dataset.requestId) return;

							var stockInfo = (r2 && r2.message && r2.message.product_info) || {};
							if (stockInfo.can_add_to_cart === false) {
								advanceStockSearch(card, container, addBtn, gotoBtn, priceEl, templateItemCode, changedAttribute, skipResync);
								return;
							}

							// A buyable match - any in-progress hunt for an
							// in-stock combination is done.
							delete container._stockSearch;
							updateCardImage(card, stockInfo.image);
							setButtonResolved(card, addBtn, gotoBtn, matchedItemCode, enableCheckout, skipResync);

							if (priceEl) {
								// Card-contract shared ribbon: bold whole-unit price,
								// grey struck MRP, green "N% OFF".
								priceEl.innerHTML =
									(info && info.price && window.webhqPriceRibbon)
										? window.webhqPriceRibbon(info.price)
										: '';
							}
						}
					});
				} else if (container._stockSearch) {
					// This combination doesn't exist as a real item either -
					// keep hunting rather than abandoning the search here.
					advanceStockSearch(card, container, addBtn, gotoBtn, priceEl, templateItemCode, changedAttribute, skipResync);
				} else {
					// No single exact match for the current pick (e.g. a sparse
					// variant matrix where the first value of every attribute
					// doesn't combine into a real item). Keep the button reading
					// "Add to Cart"/"Add to Quote" regardless - it stays pending
					// (non-clickable, see the capture-phase guard below) until a
					// match resolves, but never reverts to "Select Options" wording.
					setButtonPending(card, addBtn, gotoBtn, enableCheckout ? __("Add to Cart") : __("Add to Quote"));
					if (priceEl) priceEl.innerHTML = '';
				}
			}
		});
	}

	// Pill selection (single choice per attribute row), scoped to the card it was clicked in.
	document.addEventListener('click', function (e) {
		var pill = e.target.closest('.variant-pill');
		if (!pill || pill.classList.contains('variant-pill-disabled') || pill.classList.contains('variant-pill-oos')) return;

		var row = pill.closest('.variant-attr-row');
		// Resolve via card first, not pill.closest('.variant-swatches') - a
		// colour pill lives in .card-img-container, not .variant-swatches, so
		// .closest() from the pill itself would miss it.
		var card = pill.closest('.item-card, .list-row');
		var container = card && card.querySelector('.variant-swatches');
		if (!row || !container || !card) return;

		// The shopper is changing context - any out-of-stock pills we
		// discovered under the *previous* combination aren't necessarily
		// still out of stock under this one, so let them be rediscovered.
		clearOosPills(card);
		delete container._stockSearch;

		Array.prototype.forEach.call(row.querySelectorAll('.variant-pill'), function (p) {
			p.classList.toggle('active', p === pill);
		});
		container.dataset.selectionSource = 'user';

		resolveVariant(card, container.getAttribute('data-template-item-code'), undefined, row.getAttribute('data-attribute'));
	});

	// Block Add to Cart clicks until a full variant match is resolved (capture phase
	// so this runs before the bubble-phase delegated handlers in shopping_cart.js /
	// listing_quantity.js see the click).
	document.addEventListener('click', function (e) {
		var btn = e.target.closest('.variant-add-btn.variant-pending');
		if (!btn) return;

		e.preventDefault();
		e.stopPropagation();
		if (e.stopImmediatePropagation) e.stopImmediatePropagation();

		var card = btn.closest('.item-card, .list-row');
		var container = card && card.querySelector('.variant-swatches');
		if (container) {
			container.classList.add('variant-attention');
			setTimeout(function () {
				container.classList.remove('variant-attention');
			}, 400);
		}
	}, true);

	// --- Boot: scan whatever is already on the page, then keep watching for
	// more cards appearing anywhere, from any source, for as long as the
	// page lives (infinite scroll, filter/sort re-renders, AJAX sections).

	scanForCards(document.body);

	window.webhqDom.onAdded(function (roots) {
		roots.forEach(function (node) {
			scanForCards(node);
		});
	});

	document.addEventListener('products-loaded', function () {
		scanForCards(document.body);
	});

	// Keep pre-selection in sync with the cart: fetch it once up front, and
	// again whenever the cart changes (including from our own resolved
	// Add to Cart clicks, via the same 'cart-updated' event listing_quantity.js
	// already triggers/listens for).
	refreshCartVariantSelections();
	if (window.jQuery && typeof window.jQuery === 'function') {
		window.jQuery(document).on('cart-updated', refreshCartVariantSelections);
	}
});
